import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Clock, DollarSign, MapPin, ChevronLeft, Shield, Users, Crown, Skull, Filter } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const FACTION_STYLES = {
  cmd: { name: 'CMD', color: '#C9A84C', bg: 'rgba(201,168,76,0.1)', border: 'rgba(201,168,76,0.3)' },
  morelli: { name: 'MORELLI', color: '#8B1A1A', bg: 'rgba(139,26,26,0.1)', border: 'rgba(139,26,26,0.3)' },
  ddn: { name: 'DDN', color: '#4A7AB5', bg: 'rgba(74,122,181,0.1)', border: 'rgba(74,122,181,0.3)' },
};

const formatDuration = (secs) => {
  if (!secs || secs <= 0) return '--';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const FactionBadge = ({ faction }) => {
  const style = FACTION_STYLES[faction] || FACTION_STYLES.cmd;
  return (
    <span
      className="text-[9px] tracking-widest px-1.5 py-0.5 rounded font-['Outfit'] font-semibold"
      style={{ color: style.color, background: style.bg, border: `1px solid ${style.border}` }}
      data-testid={`faction-badge-${faction}`}
    >
      {style.name}
    </span>
  );
};

// --- Leaderboard Entry Row ---
const EntryRow = ({ entry, rank, category }) => {
  const fStyle = FACTION_STYLES[entry.faction] || FACTION_STYLES.cmd;
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.03 }}
      className={`flex items-center gap-3 px-4 py-3 border-b border-[#1A2535]/50 hover:bg-[#111820]/80 transition-colors ${rank < 3 ? 'bg-[#111820]/40' : ''}`}
      data-testid={`leaderboard-entry-${rank}`}
    >
      <div className="w-8 text-center shrink-0">
        {rank === 0 && <Crown size={16} className="text-[#C9A84C] mx-auto" />}
        {rank === 1 && <Crown size={14} className="text-[#888] mx-auto" />}
        {rank === 2 && <Crown size={14} className="text-[#8B5E3C] mx-auto" />}
        {rank > 2 && <span className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">{rank + 1}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-['Outfit'] text-[#E8E0D0] text-sm font-semibold truncate">{entry.username}</span>
          <FactionBadge faction={entry.faction} />
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px]">{entry.path_name}</span>
          <span className="font-['Outfit'] text-[10px]" style={{ color: fStyle.color + '80' }}>Lv.{entry.level} {entry.level_title}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        {category === 'speed' && (
          <span className="font-['Outfit'] text-[#C9A84C] text-sm font-bold" data-testid="entry-speed-value">
            {formatDuration(entry.duration_seconds)}
          </span>
        )}
        {category === 'wealth' && (
          <div>
            <span className="font-['Outfit'] text-[#C9A84C] text-sm font-bold" data-testid="entry-wealth-value">
              ${entry.cash?.toLocaleString()}
            </span>
            {entry.game_complete && <Skull size={10} className="inline ml-1 text-[#8B1A1A]" />}
          </div>
        )}
        {category === 'territory' && (
          <div>
            <span className="font-['Outfit'] text-[#C9A84C] text-sm font-bold" data-testid="entry-territory-value">
              {entry.territory_count} blocks
            </span>
            {entry.game_complete && <Skull size={10} className="inline ml-1 text-[#8B1A1A]" />}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// --- Faction War Panel ---
const FactionWarPanel = ({ factions, totalGames }) => {
  if (!factions || factions.length === 0) {
    return (
      <div className="text-center py-12">
        <Shield size={32} className="text-[#E8E0D0]/20 mx-auto mb-3" />
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-sm">No faction data yet. Start playing to claim territory.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs tracking-widest">GLOBAL TERRITORY WAR</p>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[10px]">{totalGames} active games</p>
      </div>

      {/* Faction control bar */}
      <div className="h-3 rounded-full overflow-hidden flex bg-[#1A2535]" data-testid="faction-control-bar">
        {factions.map(f => (
          f.control_pct > 0 && (
            <div
              key={f.faction}
              className="h-full transition-all duration-700"
              style={{ width: `${Math.max(f.control_pct, 2)}%`, backgroundColor: f.faction_color }}
              title={`${f.faction_name}: ${f.control_pct}%`}
            />
          )
        ))}
      </div>

      {/* Faction cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {factions.map((f, i) => {
          const style = FACTION_STYLES[f.faction] || FACTION_STYLES.cmd;
          return (
            <motion.div
              key={f.faction}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#111820] border rounded-lg p-4 text-center"
              style={{ borderColor: style.border }}
              data-testid={`faction-card-${f.faction}`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield size={14} style={{ color: style.color }} />
                <h3 className="font-['Cormorant_Garamond'] font-bold text-lg" style={{ color: style.color }}>
                  {f.faction_name}
                </h3>
              </div>
              <p className="font-['Outfit'] text-2xl font-bold" style={{ color: style.color }}>
                {f.control_pct}%
              </p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest mt-1">TERRITORY CONTROL</p>
              <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t" style={{ borderColor: style.border + '20' }}>
                <div>
                  <p className="font-['Outfit'] text-[#E8E0D0] text-sm font-bold">{f.total_territories}</p>
                  <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">BLOCKS</p>
                </div>
                <div>
                  <p className="font-['Outfit'] text-[#E8E0D0] text-sm font-bold">{f.players}</p>
                  <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">SOLDIERS</p>
                </div>
              </div>
              {f.boroughs && Object.keys(f.boroughs).length > 0 && (
                <div className="mt-3 pt-3 border-t text-left" style={{ borderColor: style.border + '15' }}>
                  {Object.entries(f.boroughs).slice(0, 4).map(([borough, count]) => (
                    <div key={borough} className="flex justify-between py-0.5">
                      <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] truncate mr-2">{borough}</span>
                      <span className="font-['Outfit'] text-[10px] shrink-0" style={{ color: style.color + '99' }}>{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};


// --- Main Leaderboard Page ---
const LeaderboardPage = () => {
  const [category, setCategory] = useState('wealth');
  const [pathFilter, setPathFilter] = useState(null);
  const [entries, setEntries] = useState([]);
  const [factionData, setFactionData] = useState(null);
  const [loading, setLoading] = useState(false);

  const categories = [
    { id: 'wealth', label: 'WEALTH', icon: <DollarSign size={14} /> },
    { id: 'territory', label: 'TERRITORY', icon: <MapPin size={14} /> },
    { id: 'speed', label: 'SPEED RUN', icon: <Clock size={14} /> },
    { id: 'factions', label: 'FACTION WAR', icon: <Shield size={14} /> },
  ];

  const paths = [
    { id: null, label: 'ALL' },
    { id: 'anton', label: 'ANTON' },
    { id: 'vincent', label: 'VINCENT' },
    { id: 'diego', label: 'DIEGO' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (category === 'factions') {
          const res = await fetch(`${API}/api/game/leaderboard/factions`);
          const data = await res.json();
          setFactionData(data);
        } else {
          const pathParam = pathFilter ? `?path=${pathFilter}` : '';
          const res = await fetch(`${API}/api/game/leaderboard/${category}${pathParam}`);
          const data = await res.json();
          setEntries(data.leaderboard || data.entries || []);
        }
      } catch (err) {
        console.error('Leaderboard fetch error:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [category, pathFilter]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#E8E0D0]" data-testid="leaderboard-page">
      <div className="film-grain" />

      {/* Header */}
      <div className="relative border-b border-[#1A2535]">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <a href="/game" className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest hover:text-[#C9A84C] transition-colors flex items-center gap-1 mb-2" data-testid="back-to-game">
                <ChevronLeft size={12} /> BACK TO GAME
              </a>
              <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#C9A84C]" data-testid="leaderboard-title">
                THE LEDGER
              </h1>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs tracking-widest mt-1">THE WATER KEEPS SCORE</p>
            </div>
            <Trophy size={28} className="text-[#C9A84C]/30" />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Category Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2" data-testid="category-tabs">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded font-['Outfit'] text-[11px] tracking-widest transition-all whitespace-nowrap ${
                category === cat.id
                  ? 'bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30'
                  : 'text-[#E8E0D0]/30 border border-transparent hover:text-[#E8E0D0]/60'
              }`}
              data-testid={`tab-${cat.id}`}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>

        {/* Path Filter (not shown for factions) */}
        {category !== 'factions' && (
          <div className="flex items-center gap-2 mb-6" data-testid="path-filter">
            <Filter size={12} className="text-[#E8E0D0]/20" />
            {paths.map(p => (
              <button
                key={p.id || 'all'}
                onClick={() => setPathFilter(p.id)}
                className={`px-3 py-1 rounded font-['Outfit'] text-[10px] tracking-widest transition-all ${
                  pathFilter === p.id
                    ? 'bg-[#1A2535] text-[#C9A84C] border border-[#C9A84C]/20'
                    : 'text-[#E8E0D0]/20 hover:text-[#E8E0D0]/40'
                }`}
                data-testid={`filter-${p.id || 'all'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {category === 'factions' ? (
              <motion.div key="factions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <FactionWarPanel factions={factionData?.factions} totalGames={factionData?.total_games} />
              </motion.div>
            ) : entries.length === 0 ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
                <Users size={32} className="text-[#E8E0D0]/10 mx-auto mb-3" />
                <p className="font-['Outfit'] text-[#E8E0D0]/20 text-sm">
                  {category === 'speed' ? 'No completed runs yet. Finish all 7 eras to appear here.' : 'No entries yet. Start playing to climb the ranks.'}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key={`${category}-${pathFilter}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-[#0A0E14] border border-[#1A2535] rounded-lg overflow-hidden"
                data-testid="leaderboard-list"
              >
                {/* Table header */}
                <div className="flex items-center gap-3 px-4 py-2 border-b border-[#1A2535] bg-[#111820]/50">
                  <div className="w-8 text-center">
                    <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">#</span>
                  </div>
                  <div className="flex-1">
                    <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">PLAYER</span>
                  </div>
                  <div>
                    <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">
                      {category === 'speed' ? 'TIME' : category === 'wealth' ? 'CASH' : 'BLOCKS'}
                    </span>
                  </div>
                </div>
                {entries.map((entry, i) => (
                  <EntryRow key={entry.game_id} entry={entry} rank={i} category={category} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
